import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { clamp, DEG, damp, wrapAngle, lerp, easeOutQuad, easeInOutQuad } from '../../engine/math/easing';
import { SpringF } from '../../engine/math/spring';
import { SquashStretch } from '../../engine/anim/Tween';
import { capsule, sphere, cylinder, combine } from '../../engine/geo/primitives';
import { translate } from '../../engine/geo/mesh';
import type { Ctx, Renderable } from '../Ctx';
import type { ThirdPersonCamera } from '../camera/ThirdPersonCamera';

export const PLAYER = {
  walk: 3.6,
  runMul: 1.6,
  accelTime: 0.12,
  decelTime: 0.08,
  turnRate: 720 * DEG,
  radius: 0.32,
  height: 1.7,
  dodgeTime: 0.45,
  dodgeSpeed: 9,
  iframeStart: 0.1,
  iframeEnd: 0.35,
  gravity: 24,
  stepUp: 0.4,
  slideAngle: 35 * DEG,
  maxHearts: 3,
};

const TUNIC: [number, number, number] = [0.83, 0.36, 0.12];
const CREAM: [number, number, number] = [0.95, 0.88, 0.75];
const DARK: [number, number, number] = [0.35, 0.2, 0.12];

export type PlayerState = 'idle' | 'move' | 'dodge' | 'dead' | 'frozen';

/**
 * The player: capsule body built from primitives with procedural animation,
 * kinematic movement over the static world, dodge roll, hearts.
 */
export class Player {
  readonly entity: number;
  readonly root: Transform;
  readonly body: Transform; // scaled by squash/stretch, holds all parts
  readonly torso: Transform;
  readonly head: Transform;
  readonly armL: Transform;
  readonly armR: Transform;
  readonly legL: Transform;
  readonly legR: Transform;
  readonly hand: Transform; // held item anchor (child of armR)
  readonly parts: Renderable[] = [];
  readonly velocity = new Vec3();
  readonly position: Vec3;
  facing = 0; // yaw, 0 = +Z
  state: PlayerState = 'idle';
  grounded = true;
  groundNormal = new Vec3(0, 1, 0);
  slope = 0;
  hearts = PLAYER.maxHearts;
  invulnUntil = 0;
  dodgeT = 0;
  private dodgeDir = new Vec3(0, 0, 1);
  private gait = 0; // walk cycle phase
  private speedNorm = 0; // 0..1.6
  private lean = 0;
  readonly squash = new SquashStretch(7, 0.32);
  readonly flash = SpringF.fromFreq(0, 8, 1);
  private breath = 0;
  /** Multipliers other systems apply (water, chalice). */
  speedMul = 1;
  runMulOverride: number | null = null;
  /** Set by the item system: true when holding something. */
  holding = false;
  onDodge?: () => void;
  onLand?: (impact: number) => void;
  onStep?: () => void;
  private wasGrounded = true;
  private airTime = 0;
  private stepPhase = 0;
  noiseLevel = 0; // for the wyrm
  /** Sword swing phase 0..1, or -1 when not attacking (driven by Combat). */
  attackT = -1;
  attackCombo = 0;

  constructor(private readonly ctx: Ctx, private readonly cam: ThirdPersonCamera, spawn: Vec3) {
    this.root = new Transform();
    this.root.position.copy(spawn);
    this.position = this.root.position;
    this.entity = ctx.spawn('player', this.root);
    this.body = new Transform().setParent(this.root);
    this.body.entity = ctx.spawn('player.body', this.body);

    const r = ctx.renderer;
    const add = (name: string, parent: Transform, mesh: ReturnType<typeof capsule>, pos: Vec3): Transform => {
      const t = new Transform().setParent(parent);
      t.position.copy(pos);
      t.entity = ctx.spawn(name, t, mesh);
      this.parts.push(ctx.renderables.req(t.entity));
      return t;
    };
    void r;
    // torso: capsule radius 0.27, cylinder 0.45 → 0.99 tall, base at y=0.65
    this.torso = add('player.torso', this.body, capsule(0.27, 0.42, 10, 3, TUNIC), new Vec3(0, 1.05, 0));
    this.head = add('player.head', this.body, combine(sphere(0.23, 10, 7, CREAM), translate(sphere(0.06, 6, 4, DARK), 0.1, 0.03, 0.19), translate(sphere(0.06, 6, 4, DARK), -0.1, 0.03, 0.19)), new Vec3(0, 1.56, 0));
    // limbs pivot at joints; meshes hang downward
    const arm = () => translate(cylinder(0.065, 0.075, 0.56, 7, TUNIC), 0, -0.28, 0);
    const leg = () => translate(capsule(0.1, 0.4, 7, 2, DARK), 0, -0.3, 0);
    this.armL = add('player.armL', this.body, arm(), new Vec3(0.33, 1.32, 0));
    this.armR = add('player.armR', this.body, arm(), new Vec3(-0.33, 1.32, 0));
    this.legL = add('player.legL', this.body, leg(), new Vec3(0.15, 0.72, 0));
    this.legR = add('player.legR', this.body, leg(), new Vec3(-0.15, 0.72, 0));
    this.hand = new Transform().setParent(this.armR);
    this.hand.position.set(0, -0.6, 0.1);
    this.hand.entity = ctx.spawn('player.hand', this.hand);
    this.snapAll();
  }

  snapAll(): void {
    const walk = (t: Transform) => { t.snapPrev(); for (const c of t.children) walk(c); };
    walk(this.root);
  }

  get invulnerable(): boolean {
    if (this.ctx.time < this.invulnUntil) return true;
    return this.state === 'dodge' && this.dodgeT >= PLAYER.iframeStart && this.dodgeT <= PLAYER.iframeEnd;
  }

  teleport(p: Vec3, facing = this.facing): void {
    this.root.position.copy(p);
    this.velocity.set(0, 0, 0);
    this.facing = facing;
    this.root.rotation.setYaw(facing);
    this.snapAll();
  }

  /** Damage: returns true if it landed. */
  hurt(from: Vec3 | null, knockback = 2): boolean {
    if (this.invulnerable || this.state === 'dead' || this.state === 'frozen') return false;
    this.hearts = Math.max(0, this.hearts - 1);
    this.invulnUntil = this.ctx.time + 0.8;
    this.flash.value = 1;
    if (from) {
      const d = _v.copy(this.position).sub(from); d.y = 0; d.normalize();
      this.velocity.x = d.x * knockback * 4; this.velocity.z = d.z * knockback * 4;
    }
    this.squash.squash(0.7);
    this.ctx.fx.shake(0.25, 0.3);
    this.ctx.fx.fovKick(-6);
    this.ctx.fx.hitStop(0.06);
    this.ctx.sfx.hurt();
    return true;
  }

  update(dt: number): void {
    const ctx = this.ctx, input = ctx.input, st = ctx.statics;
    const p = this.root.position;
    if (this.state === 'dead' || this.state === 'frozen') {
      this.animate(dt, 0);
      return;
    }

    // --- input → desired velocity ---
    const [ax, ay] = input.moveAxis();
    const fwd = this.cam.flatForward(_f), right = this.cam.flatRight(_r);
    const wish = _wish.set(0, 0, 0).addScaled(fwd, ay).addScaled(right, ax);
    const hasInput = wish.lengthSq() > 0.01;
    if (hasInput) wish.normalize();
    const running = input.isDown('ShiftLeft') || input.isDown('ShiftRight');
    const runMul = this.runMulOverride ?? PLAYER.runMul;
    const targetSpeed = hasInput ? PLAYER.walk * (running ? runMul : 1) * this.speedMul : 0;

    // --- dodge ---
    if (this.state !== 'dodge' && input.wasPressed('Space') && this.grounded) {
      this.state = 'dodge';
      this.dodgeT = 0;
      this.dodgeDir.copy(hasInput ? wish : _v.set(Math.sin(this.facing), 0, Math.cos(this.facing)));
      this.facing = Math.atan2(this.dodgeDir.x, this.dodgeDir.z);
      this.ctx.fx.fovKick(8);
      this.onDodge?.();
      this.noiseLevel = Math.max(this.noiseLevel, 1);
    }

    const hv = _hv.set(this.velocity.x, 0, this.velocity.z);
    if (this.state === 'dodge') {
      this.dodgeT += dt;
      const k = this.dodgeT / PLAYER.dodgeTime;
      const speed = PLAYER.dodgeSpeed * (1 - k * 0.6) * this.speedMul;
      hv.copy(this.dodgeDir).scale(speed);
      if (this.dodgeT >= PLAYER.dodgeTime) { this.state = 'idle'; this.squash.squash(0.82); }
    } else {
      // accelerate toward wish
      const cur = hv.length();
      const accel = targetSpeed > cur ? (PLAYER.walk / PLAYER.accelTime) : (PLAYER.walk / PLAYER.decelTime);
      if (hasInput) {
        const target = _v.copy(wish).scale(targetSpeed);
        const dx = target.x - hv.x, dz = target.z - hv.z;
        const dl = Math.hypot(dx, dz);
        const step = Math.min(dl, accel * dt);
        if (dl > 1e-5) { hv.x += (dx / dl) * step; hv.z += (dz / dl) * step; }
      } else {
        const ns = Math.max(0, cur - accel * dt);
        if (cur > 1e-5) hv.scale(ns / cur); else hv.set(0, 0, 0);
      }
      // sliding on steep slopes
      if (this.grounded && this.slope > PLAYER.slideAngle) {
        const n = this.groundNormal;
        const down = _v.set(n.x, 0, n.z); // downhill direction (horizontal component of normal)
        const l = down.length();
        if (l > 1e-4) { down.scale(1 / l); hv.addScaled(down, 14 * dt * (this.slope - PLAYER.slideAngle + 0.3)); }
        hv.clampLength(12);
      }
      this.state = hasInput || cur > 0.1 ? 'move' : 'idle';
      // face movement direction
      if (hv.lengthSq() > 0.05) {
        const want = Math.atan2(hv.x, hv.z);
        const diff = wrapAngle(want - this.facing);
        this.facing += clamp(diff, -PLAYER.turnRate * dt, PLAYER.turnRate * dt);
        this.lean = damp(this.lean, clamp(diff, -1, 1) * 8 * DEG * Math.min(1, hv.length() / PLAYER.walk), 0.08, dt);
      } else this.lean = damp(this.lean, 0, 0.08, dt);
    }
    this.velocity.x = hv.x; this.velocity.z = hv.z;

    // --- integrate & collide ---
    p.x += this.velocity.x * dt;
    p.z += this.velocity.z * dt;
    const pushed = st.resolveCapsule(p, PLAYER.radius, PLAYER.height, PLAYER.stepUp, _push);
    if (pushed) {
      // slide: remove velocity into the wall
      const into = this.velocity.dot(_push);
      if (into < 0) this.velocity.addScaled(_push, -into);
    }
    // ground
    const g = st.ground(p.x, p.y, p.z, PLAYER.stepUp, _ground);
    this.slope = Math.acos(clamp(g.normal.y, -1, 1));
    if (p.y <= g.y + 0.05 || (this.grounded && p.y - g.y < PLAYER.stepUp + 0.05 && this.velocity.y <= 0)) {
      // snap to ground
      if (!this.wasGrounded && this.airTime > 0.15) {
        const impact = Math.min(1, -this.velocity.y / 12);
        this.squash.squash(1 - 0.25 * impact);
        this.onLand?.(impact);
      }
      p.y = g.y;
      this.velocity.y = 0;
      this.grounded = true;
      this.groundNormal.copy(g.normal);
      this.airTime = 0;
    } else {
      this.grounded = false;
      this.velocity.y -= PLAYER.gravity * dt;
      p.y += this.velocity.y * dt;
      this.airTime += dt;
      if (p.y < g.y) { p.y = g.y; this.velocity.y = 0; this.grounded = true; }
    }
    this.wasGrounded = this.grounded;

    // orientation
    this.root.rotation.setYaw(this.facing);
    const speedNow = Math.hypot(this.velocity.x, this.velocity.z);
    this.speedNorm = speedNow / PLAYER.walk;
    this.noiseLevel = Math.max(this.noiseLevel - dt * 0.8, running && hasInput ? 0.7 : 0);
    this.animate(dt, speedNow);
  }

  /** Procedural animation (§3.3): gait, breathing, lean, dodge tuck, hit flash. */
  private animate(dt: number, speed: number): void {
    const s = this.squash.update(dt);
    this.body.scale.copy(s);
    this.flash.target = 0;
    const fl = this.flash.update(dt);
    const blink = this.ctx.time < this.invulnUntil ? (Math.sin(this.ctx.time * 40) > 0 ? 0.8 : 0) : 0;
    for (const r of this.parts) r.flash = Math.max(fl, blink);

    const dodging = this.state === 'dodge';
    if (dodging) {
      const k = this.dodgeT / PLAYER.dodgeTime;
      // tuck: torso becomes a ball, limbs hidden, roll once about the horizontal axis perpendicular to travel
      // pivot the whole body around the ball's center so the roll stays above ground
      this.body.position.set(0, 0.6, 0);
      this.torso.scale.set(1.25, 0.72, 1.25);
      this.torso.position.set(0, 0, 0);
      this.head.scale.set(0.85, 0.85, 0.85);
      this.head.position.set(0, 0.42, 0.2);
      for (const t of [this.armL, this.armR, this.legL, this.legR]) t.visible = false;
      this.body.rotation.setAxisAngle(_x, k * Math.PI * 2);
      return;
    }
    this.body.position.set(0, 0, 0);
    for (const t of [this.armL, this.armR, this.legL, this.legR]) t.visible = true;
    this.torso.scale.set(1, 1, 1);
    this.head.scale.set(1, 1, 1);

    // lean into turns, roll axis Z (sideways) + slight pitch forward when running
    const pitchFwd = clamp(speed / (PLAYER.walk * PLAYER.runMul), 0, 1) * 6 * DEG;
    this.body.rotation.setEuler(0, pitchFwd, -this.lean);

    // gait
    const moving = speed > 0.2;
    const freq = moving ? 1.6 + speed * 0.55 : 0;
    this.gait += freq * dt * Math.PI * 2;
    const amp = clamp(speed / PLAYER.walk, 0, 1.4);
    const swing = Math.sin(this.gait) * 0.75 * amp;
    this.legL.rotation.setAxisAngle(_x, swing);
    this.legR.rotation.setAxisAngle(_x, -swing);
    const armSwing = swing * 0.7;
    this.armL.rotation.setEuler(0, -armSwing, 12 * DEG * (0.5 + amp * 0.5));
    let twist = 0;
    if (this.attackT >= 0 && this.holding) {
      // anticipation → sweep → recover; alternate direction per combo hit
      const k = this.attackT, dir = this.attackCombo % 2 === 0 ? 1 : -1;
      let pitch: number, yaw: number;
      if (k < 0.28) { const a = k / 0.28; pitch = lerp(-55, -165, easeOutQuad(a)); yaw = lerp(0, 40 * dir, a); }
      else if (k < 0.6) { const a = (k - 0.28) / 0.32; pitch = lerp(-165, -5, easeInOutQuad(a)); yaw = lerp(40 * dir, -60 * dir, a); }
      else { const a = (k - 0.6) / 0.4; pitch = lerp(-5, -55, easeOutQuad(a)); yaw = lerp(-60 * dir, 0, a); }
      this.armR.rotation.setEuler(yaw * DEG, pitch * DEG, -10 * DEG);
      twist = yaw * 0.45 * DEG;
    } else if (this.holding) this.armR.rotation.setEuler(0, -55 * DEG, -10 * DEG);
    else this.armR.rotation.setEuler(0, armSwing, -12 * DEG * (0.5 + amp * 0.5));
    if (twist !== 0) this.body.rotation.setEuler(twist, pitchFwd, -this.lean);
    // footsteps
    const phase = Math.sin(this.gait);
    if (moving && this.grounded && ((phase > 0 && this.stepPhase <= 0) || (phase < 0 && this.stepPhase >= 0))) this.onStep?.();
    this.stepPhase = phase;

    // bob + breathing
    this.breath += dt;
    const bob = moving ? Math.abs(Math.sin(this.gait)) * 0.03 * amp : 0;
    const breathe = moving ? 1 : 1 + Math.sin(this.breath * (Math.PI * 2) / 4) * 0.02;
    this.torso.position.set(0, 1.05 + bob, 0);
    this.torso.scale.set(1 / Math.sqrt(breathe), breathe, 1 / Math.sqrt(breathe));
    this.head.position.set(0, 1.56 + bob * 1.2, moving ? 0.03 : 0);
    this.head.rotation.setEuler(moving ? Math.sin(this.gait * 0.5) * 0.05 : Math.sin(this.breath * 0.7) * 0.06, 0, 0);
  }
}
const _f = new Vec3(), _r = new Vec3(), _wish = new Vec3(), _v = new Vec3(), _hv = new Vec3(), _push = new Vec3();
const _x = new Vec3(1, 0, 0);
const _ground = { y: 0, normal: new Vec3(0, 1, 0), floor: null as null };
